import * as React from 'react'

import { Box, Text } from '@factorialco/deprecated-design-system'
import {
  EmployeeTimeoffPoliciesFromLeavesDocument,
  LeaveEmployeeDataFragment,
} from 'generated/resources/graphql'
import { reaction } from 'mobx'
import moment from 'moment'

import { observer } from 'mobx-react'

import BoxList from 'modules/core/components/BoxList'
import CheckControl from 'modules/core/components/Inputs/CheckControl'
import DayPicker from 'modules/core/components/Inputs/DayPicker'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useGraphQLClient } from 'modules/core/lib/graphql/provider'
import i18n from 'modules/core/lib/i18n'

import type LeaveForm from 'modules/timeoff/lib/LeaveForm'
import leaves from 'modules/timeoff/store/collections/leaves'

import Item from './Item'
import type { RelapseOption } from './Item'

interface Props {
  leaveEmployee: LeaveEmployeeDataFragment
  form: LeaveForm
  year: number
  readOnly: boolean
}

const Relapse: React.FC<Props> = ({ leaveEmployee, form, year, readOnly }) => {
  const graphqlClient = useGraphQLClient()

  if (!graphqlClient) {
    throw new Error('GraphQL client not found')
  }

  const { value: leavesOptions } = useAsyncData<RelapseOption[]>(async () => {
    const filteredLeaves = leaves.models.filter((l) => {
      const finishOn = moment(l.attributes.finish_on)
      const today = moment()

      return (
        l.leaveType?.isMedicalLeave &&
        leaveEmployee.id === l.attributes.employee_id &&
        finishOn.isBefore(today)
      )
    })

    const { data: employeeTimeoffPoliciesFromLeavesData } = await graphqlClient.query({
      query: EmployeeTimeoffPoliciesFromLeavesDocument,
      variables: {
        leaveIds: filteredLeaves.map((leave: { id: string }) => leave.id),
      },
    })

    return filteredLeaves.map((leave) => ({
      field: form.get('relapse_leave_id'),
      name: 'relapse_leave',
      leave: leave,
      year: year,
      value: leave.attributes.id,
      employeeTimeoffPolicyId: employeeTimeoffPoliciesFromLeavesData?.timeoff.leaves.find(
        (leaveData) => leaveData.id === leave.attributes.id
      )?.employee?.timeoffPolicy?.id,
    }))
  }, [leaveEmployee.id, form.get('relapse_leave_id').value, year])

  React.useEffect(() => {
    const handler = reaction(
      () => form.get('has_previous_relapse').value,
      (hasPreviousRelapse) => {
        if (hasPreviousRelapse) return

        form.setValues({
          relapse_on: null,
          relapse_leave_id: null,
        })
      }
    )

    const handlerRelapseLeave = reaction(
      () => form.get('relapse_leave_id').value,
      (relapseLeaveId) => {
        if (!relapseLeaveId) return

        const leave = leaves.find({
          id: relapseLeaveId,
        })

        if (!leave) {
          throw Error(`Error finding leaves with id = ${relapseLeaveId}`)
        }

        form.setValues({
          relapse_on: leave.attributes.start_on,
        })
      }
    )

    return () => {
      handler()
      handlerRelapseLeave()
    }
  }, [form])

  const renderRelapseSelector = React.useCallback(() => {
    if (!form.get('has_previous_relapse').value) return null

    const readOnlyRelapseOn = readOnly || !!form.get('relapse_leave_id').value

    return (
      <Box paddingTop="s24">
        {leavesOptions && leavesOptions.length > 0 ? (
          <>
            <BoxList>
              {leavesOptions.map((relapse) => (
                <Item key={relapse.leave.attributes.id} {...relapse} />
              ))}
            </BoxList>
            <Text.Normal color="grey800">
              {i18n.t('time_off.medical_leave.relapse.description')}
            </Text.Normal>
          </>
        ) : null}
        <DayPicker
          field={form.get('relapse_on')}
          label={i18n.t('time_off.medical_leave.relapse.label')}
          readonly={readOnlyRelapseOn}
        />
      </Box>
    )
  }, [form, readOnly, leavesOptions])

  return (
    <>
      <CheckControl
        field={form.get('has_previous_relapse')}
        behaviour="checkbox"
        readonly={readOnly}
        skin="checkbox"
      >
        {() => i18n.t('time_off.medical_leave.relapse.has_previous_relapse')}
      </CheckControl>
      {renderRelapseSelector()}
    </>
  )
}

export default observer(Relapse)
